import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  extractPlanSummary,
  extractReadinessSummary,
  formatDateTime,
  formatPrLinkLabel,
  formatPrSectionTitle,
  formatPrStatusLabel,
  formatSourceLabel,
  getGenerationGatingState,
  hasPrActivity,
  isMockPrEvidence,
  isTransientDisplayStatus,
  POLL_MS,
  prStatusChipColor,
  resolveActionLabel,
  sanitizeCaseForDisplay,
  shouldShowPrLink,
  statusChipColor,
  filterUiActions,
} from './automationCaseUtils';

const SECTION_IDS = {
  overview: 'automation-case-section-overview',
  readiness: 'automation-case-section-readiness',
  plan: 'automation-case-section-plan',
  generation: 'automation-case-section-generation',
  execution: 'automation-case-section-execution',
  audit: 'automation-case-section-audit',
  pr: 'automation-case-section-pr',
};

function Section({ title, children, id, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box id={id} sx={{ mb: 2 }}>
      <Button
        onClick={() => setOpen((v) => !v)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ justifyContent: 'flex-start', px: 0, textTransform: 'none', fontWeight: 600 }}
      >
        {title}
      </Button>
      <Collapse in={open}>{children}</Collapse>
    </Box>
  );
}

const AutomationCaseDetailDrawer = ({
  open,
  caseId,
  listCaseRow = null,
  refreshToken = 0,
  initialSection = 'overview',
  apiClient,
  onClose,
  onAction,
  busy = false,
}) => {
  const [caseDetail, setCaseDetail] = useState(null);
  const [generationJob, setGenerationJob] = useState(null);
  const [executionJob, setExecutionJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);

  const loadDetail = async () => {
    if (!caseId || !apiClient?.getAutomationCase) return;
    setLoading(true);
    setError(null);
    try {
      const detail = sanitizeCaseForDisplay(await apiClient.getAutomationCase(caseId));
      setCaseDetail(detail);
      const genId = detail.latest_generation_job_id;
      const execId = detail.latest_execution_job_id;
      if (genId && apiClient.getAutomationGenerationJob) {
        try {
          setGenerationJob(await apiClient.getAutomationGenerationJob(genId));
        } catch {
          setGenerationJob(null);
        }
      } else {
        setGenerationJob(null);
      }
      if (execId && apiClient.getAutomationExecutionJob) {
        try {
          setExecutionJob(await apiClient.getAutomationExecutionJob(execId));
        } catch {
          setExecutionJob(null);
        }
      } else {
        setExecutionJob(null);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load automation case details.');
      setCaseDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && caseId) {
      loadDetail();
    } else {
      setCaseDetail(null);
      setGenerationJob(null);
      setExecutionJob(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseId]);

  useEffect(() => {
    if (!open || !caseId || refreshToken === 0) return;
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    if (!open || !listCaseRow?.case_id || listCaseRow.case_id !== caseId) return;
    setCaseDetail((prev) => {
      if (!prev || prev.case_id !== listCaseRow.case_id) return prev;
      return sanitizeCaseForDisplay({ ...prev, ...listCaseRow });
    });
  }, [open, caseId, listCaseRow]);

  useEffect(() => {
    if (!open || !caseId || !caseDetail || !apiClient?.getAutomationCase) return undefined;
    if (!isTransientDisplayStatus(caseDetail.display_status)) return undefined;

    let cancelled = false;
    const timer = setInterval(async () => {
      if (cancelled) return;
      try {
        const updated = sanitizeCaseForDisplay(await apiClient.getAutomationCase(caseId));
        if (cancelled) return;
        setCaseDetail(updated);
        if (updated.latest_generation_job_id && apiClient.getAutomationGenerationJob) {
          const gen = await apiClient.getAutomationGenerationJob(updated.latest_generation_job_id);
          if (!cancelled) setGenerationJob(gen);
        }
        if (updated.latest_execution_job_id && apiClient.getAutomationExecutionJob) {
          const exec = await apiClient.getAutomationExecutionJob(updated.latest_execution_job_id);
          if (!cancelled) setExecutionJob(exec);
        }
      } catch {
        // keep polling quietly
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [open, caseId, caseDetail?.display_status, apiClient]); // eslint-disable-line react-hooks/exhaustive-deps -- poll on status transitions only

  useEffect(() => {
    if (!open || !initialSection) return undefined;
    const sectionId = SECTION_IDS[initialSection];
    if (!sectionId) return undefined;
    const timer = setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
    return () => clearTimeout(timer);
  }, [open, initialSection, caseDetail?.case_id]);

  const readiness = useMemo(
    () => extractReadinessSummary(caseDetail?.readiness_snapshot_json),
    [caseDetail?.readiness_snapshot_json]
  );
  const plan = useMemo(
    () => extractPlanSummary(caseDetail?.automation_plan_snapshot_json),
    [caseDetail?.automation_plan_snapshot_json]
  );
  const gating = useMemo(() => getGenerationGatingState(caseDetail), [caseDetail]);

  const allowedActions = filterUiActions(caseDetail?.allowed_actions || []);
  const specPath = generationJob?.generated_files?.find((f) => f.path?.endsWith('.spec.ts'))?.path
    || Object.keys(generationJob?.generated_file_manifest || {}).find((p) => p.endsWith('.spec.ts'))
    || executionJob?.generated_spec_path;

  const renderActionButtons = () => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {allowedActions
        .filter((action) => action !== 'view_details')
        .map((action) => (
          <Button
            key={action}
            size="small"
            variant={['run', 'run_again', 'generate', 'approve'].includes(action) ? 'contained' : 'outlined'}
            disabled={busy}
            onClick={() => onAction?.(caseDetail, action)}
            data-testid={`case-action-${action}`}
          >
            {resolveActionLabel(action, gating)}
          </Button>
        ))}
    </Stack>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="automation-case-detail-drawer"
      PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 560 }, p: 2 } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, pr: 1 }}>
          {caseDetail?.title || 'Automation Case'}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close case details">
          <CloseIcon />
        </IconButton>
      </Stack>

      {loading && !caseDetail ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {caseDetail ? (
        <>
          <Chip
            label={caseDetail.display_status}
            color={statusChipColor(caseDetail.display_status)}
            size="small"
            sx={{ mb: 1, alignSelf: 'flex-start' }}
            data-testid="case-detail-display-status"
          />
          {hasPrActivity(caseDetail) ? (
            <Chip
              label={formatPrStatusLabel(caseDetail.latest_pr_status, {
                isMock: isMockPrEvidence(caseDetail),
              })}
              color={prStatusChipColor(caseDetail.latest_pr_status, {
                isMock: isMockPrEvidence(caseDetail),
              })}
              variant={isMockPrEvidence(caseDetail) ? 'outlined' : 'filled'}
              size="small"
              sx={{ mb: 2, alignSelf: 'flex-start', ml: 0 }}
              data-testid="case-detail-pr-status"
            />
          ) : null}
          {gating.banner ? (
            <Alert
              severity={gating.kind === 'hard_blocked' ? 'error' : 'info'}
              sx={{ mb: 2 }}
              data-testid="case-clarification-gating-banner"
            >
              {gating.banner}
              {gating.blockers?.length ? (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {gating.blockers.map((blocker) => (
                    <li key={blocker}>
                      <Typography variant="body2" component="span">{blocker}</Typography>
                    </li>
                  ))}
                </Box>
              ) : null}
            </Alert>
          ) : null}
          {renderActionButtons()}

          <Section title="Overview" id={SECTION_IDS.overview}>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText primary="Source" secondary={formatSourceLabel(caseDetail.source_type)} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Created" secondary={formatDateTime(caseDetail.created_at)} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Updated" secondary={formatDateTime(caseDetail.updated_at)} />
              </ListItem>
              {caseDetail.created_by ? (
                <ListItem disableGutters>
                  <ListItemText primary="Created by" secondary={caseDetail.created_by} />
                </ListItem>
              ) : null}
            </List>
            {(caseDetail.warnings || []).length > 0 ? (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {caseDetail.warnings.map((warning) => (
                  <Alert key={warning} severity="warning">{warning}</Alert>
                ))}
              </Stack>
            ) : null}
            {caseDetail.sanitized_preview ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {caseDetail.sanitized_preview}
              </Typography>
            ) : null}
          </Section>

          <Divider sx={{ my: 1 }} />

          <Section title="Readiness" id={SECTION_IDS.readiness}>
            {readiness ? (
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Status:</strong> {readiness.status || '—'}</Typography>
                {readiness.summary ? (
                  <Typography variant="body2" color="text.secondary">{readiness.summary}</Typography>
                ) : null}
                {readiness.blockers?.length ? (
                  <Box>
                    <Typography variant="subtitle2">Blockers</Typography>
                    <List dense>{readiness.blockers.map((b) => <ListItem key={b}><ListItemText primary={b} /></ListItem>)}</List>
                  </Box>
                ) : null}
                {readiness.warnings?.length ? (
                  <Box>
                    <Typography variant="subtitle2">Warnings</Typography>
                    <List dense>{readiness.warnings.map((w) => <ListItem key={w}><ListItemText primary={w} /></ListItem>)}</List>
                  </Box>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No readiness analysis yet.</Typography>
            )}
          </Section>

          <Section title="Automation Plan" id={SECTION_IDS.plan}>
            {plan ? (
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Plan status:</strong> {plan.status || '—'}</Typography>
                <Typography variant="body2"><strong>Generation readiness:</strong> {plan.readiness || '—'}</Typography>
                {plan.framework ? (
                  <Typography variant="body2"><strong>Framework:</strong> {plan.framework}</Typography>
                ) : null}
                {plan.dependencies?.length ? (
                  <Typography variant="body2"><strong>Dependencies / risks:</strong> {plan.dependencies.join(', ')}</Typography>
                ) : null}
                {plan.clarificationQuestions?.length ? (
                  <Box data-testid="case-plan-clarifications">
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Clarification questions</Typography>
                    <List dense>
                      {plan.clarificationQuestions.map((question) => (
                        <ListItem key={question} disableGutters>
                          <ListItemText primary={question} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : null}
                {plan.cleanup ? (
                  <Typography variant="body2"><strong>Cleanup:</strong> {plan.cleanup}</Typography>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">Automation plan will appear after analysis.</Typography>
            )}
          </Section>

          <Section title="Generation" id={SECTION_IDS.generation} defaultOpen={initialSection === 'generation'}>
            {caseDetail.latest_generation_job_id ? (
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Job ID:</strong> {caseDetail.latest_generation_job_id}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {generationJob?.status || '—'}</Typography>
                {specPath ? (
                  <Typography variant="body2"><strong>Spec path:</strong> {specPath}</Typography>
                ) : null}
                {generationJob?.generation_input_sha256 ? (
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <strong>Artifact SHA:</strong> {generationJob.generation_input_sha256}
                  </Typography>
                ) : null}
                {generationJob?.safe_error ? (
                  <Alert severity="error">{generationJob.safe_error}</Alert>
                ) : null}
                {generationJob?.artifact_available && generationJob?.generated_files?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    {generationJob.generated_files.length} generated file(s) available via generation job API.
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No generation job yet.</Typography>
            )}
          </Section>

          <Section title="Execution History" id={SECTION_IDS.execution} defaultOpen={['execution', 'failure'].includes(initialSection)}>
            {caseDetail.latest_execution_job_id ? (
              <Stack spacing={1}>
                <Typography variant="body2"><strong>Latest job ID:</strong> {caseDetail.latest_execution_job_id}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {executionJob?.status || caseDetail.last_execution_status || '—'}</Typography>
                {executionJob?.duration_ms != null ? (
                  <Typography variant="body2"><strong>Duration:</strong> {executionJob.duration_ms} ms</Typography>
                ) : null}
                {executionJob?.failure_category ? (
                  <Alert severity="error">
                    {executionJob.failure_category}
                    {executionJob.safe_error ? `: ${executionJob.safe_error}` : ''}
                  </Alert>
                ) : null}
                {formatDateTime(caseDetail.last_execution_at) !== '—' ? (
                  <Typography variant="body2"><strong>Last run:</strong> {formatDateTime(caseDetail.last_execution_at)}</Typography>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No executions yet.</Typography>
            )}
          </Section>

          <Section title="Audit / Event Timeline" id={SECTION_IDS.audit} defaultOpen={false}>
            <Typography variant="body2" color="text.secondary">
              History will appear here when case events are exposed in the API.
            </Typography>
          </Section>

          <Section title={formatPrSectionTitle(caseDetail)} id={SECTION_IDS.pr} defaultOpen={initialSection === 'pr'}>
            {hasPrActivity(caseDetail) ? (
              <Stack spacing={1}>
                {isMockPrEvidence(caseDetail) ? (
                  <Alert severity="info" data-testid="case-detail-pr-mock-banner">
                    This is mock/dev PR evidence from Slice A/B smoke — not a real GitHub pull request.
                  </Alert>
                ) : null}
                <Typography variant="body2">
                  <strong>PR status:</strong>{' '}
                  {formatPrStatusLabel(caseDetail.latest_pr_status, {
                    isMock: isMockPrEvidence(caseDetail),
                  }) || '—'}
                </Typography>
                {caseDetail.latest_pr_provider && !isMockPrEvidence(caseDetail) ? (
                  <Typography variant="body2">
                    <strong>Provider:</strong> {caseDetail.latest_pr_provider}
                  </Typography>
                ) : null}
                {caseDetail.latest_pr_branch ? (
                  <Typography variant="body2">
                    <strong>Branch:</strong> {caseDetail.latest_pr_branch}
                  </Typography>
                ) : null}
                {caseDetail.latest_pr_number != null ? (
                  <Typography variant="body2">
                    <strong>PR number:</strong> #{caseDetail.latest_pr_number}
                  </Typography>
                ) : null}
                {caseDetail.latest_pr_url && shouldShowPrLink(caseDetail) ? (
                  <Link
                    href={caseDetail.latest_pr_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="case-detail-pr-link"
                  >
                    {formatPrLinkLabel(caseDetail)}
                  </Link>
                ) : null}
                {isMockPrEvidence(caseDetail) && caseDetail.latest_pr_url ? (
                  <Typography variant="body2" color="text.secondary" data-testid="case-detail-pr-mock-url">
                    <strong>Mock URL:</strong> {caseDetail.latest_pr_url}
                  </Typography>
                ) : null}
                {caseDetail.latest_pr_created_at ? (
                  <Typography variant="body2" color="text.secondary">
                    <strong>PR created:</strong> {formatDateTime(caseDetail.latest_pr_created_at)}
                  </Typography>
                ) : null}
                {caseDetail.latest_pr_failure_category || caseDetail.latest_pr_failure_message ? (
                  <Alert severity="warning" data-testid="case-detail-pr-failure">
                    {caseDetail.latest_pr_failure_category
                      ? caseDetail.latest_pr_failure_category.replace(/_/g, ' ')
                      : 'PR publication failed'}
                    {caseDetail.latest_pr_failure_message
                      ? `: ${caseDetail.latest_pr_failure_message}`
                      : ''}
                  </Alert>
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No pull request published yet.</Typography>
            )}
          </Section>

          <Button
            size="small"
            onClick={() => setDebugOpen((v) => !v)}
            sx={{ mt: 1 }}
            data-testid="toggle-case-debug"
          >
            {debugOpen ? 'Hide debug details' : 'Show debug details'}
          </Button>
          <Collapse in={debugOpen}>
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 240,
              }}
              data-testid="case-debug-json"
            >
              {JSON.stringify(sanitizeCaseForDisplay(caseDetail), null, 2)}
            </Box>
          </Collapse>
        </>
      ) : null}
    </Drawer>
  );
};

export default AutomationCaseDetailDrawer;
